import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Person, 
  Phone, 
  Email, 
  LocationOn, 
  Work, 
  AttachMoney,
  ExpandMore,
  Schedule,
  Delete,
  SwapHoriz
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { CustomerProfile } from '../types';

interface CustomerProfileDialogProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  onDelete?: () => void;
}

const CustomerProfileDialog: React.FC<CustomerProfileDialogProps> = ({
  open,
  onClose,
  customerId,
  onDelete
}) => {
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [providers, setProviders] = useState<any[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [transferForm, setTransferForm] = useState({
    newProviderId: '',
    reason: '',
    notes: '',
    customFeePercentage: '',
  });

  const fetchCustomerProfile = useCallback(async () => {
    if (!customerId) {
      console.warn('[CUSTOMER-PROFILE] ⚠️ No customerId provided');
      return;
    }
    
    console.log(`[CUSTOMER-PROFILE] 🔄 Fetching profile for customer: ${customerId}`);
    setLoading(true);
    setError(null);

    try {
      console.log(`[CUSTOMER-PROFILE] 📡 Making API call to: /customers/${customerId}/profile`);
      const response = await apiService.get(`/customers/${customerId}/profile`);
      console.log(`[CUSTOMER-PROFILE] ✅ API response received:`, {
        hasResponse: !!response,
        hasData: !!response.data,
        responseKeys: response ? Object.keys(response) : [],
        dataKeys: response?.data ? Object.keys(response.data) : []
      });
      
      // Handle different response structures
      // apiService.get returns response.data (axios response.data)
      // Backend sends: {success: true, data: {customer: ..., backupBids: ...}}
      // So response = {success: true, data: {...}}
      // And response.data = {customer: ..., backupBids: ...}
      let responseData: any;
      if (response && (response as any).data && (response as any).success === true) {
        // Wrapped response: {success: true, data: {...}}
        responseData = (response as any).data;
      } else if (response && (response as any).data) {
        // response has .data but might not have success
        responseData = (response as any).data;
      } else {
        // response is already the data object
        responseData = response;
      }
      
      console.log(`[CUSTOMER-PROFILE] 📦 Response data type:`, typeof responseData);
      console.log(`[CUSTOMER-PROFILE] 📦 Response data keys:`, responseData ? Object.keys(responseData) : 'none');
      console.log(`[CUSTOMER-PROFILE] 📦 Response has customer:`, !!responseData?.customer);
      console.log(`[CUSTOMER-PROFILE] 📦 Response has backupBids:`, !!responseData?.backupBids);
      console.log(`[CUSTOMER-PROFILE] 📦 Response has currentServices:`, !!responseData?.currentServices);
      console.log(`[CUSTOMER-PROFILE] 📦 Response data.backupBids:`, responseData?.backupBids);
      console.log(`[CUSTOMER-PROFILE] 📦 Response data.currentServices:`, responseData?.currentServices);
      console.log(`[CUSTOMER-PROFILE] 📦 Response data.backupBids type:`, Array.isArray(responseData?.backupBids) ? 'array' : typeof responseData?.backupBids);
      console.log(`[CUSTOMER-PROFILE] 📦 Response data.currentServices type:`, Array.isArray(responseData?.currentServices) ? 'array' : typeof responseData?.currentServices);
      console.log(`[CUSTOMER-PROFILE] 📦 Response data.backupBids length:`, responseData?.backupBids?.length || 0);
      console.log(`[CUSTOMER-PROFILE] 📦 Response data.currentServices length:`, responseData?.currentServices?.length || 0);
      
      let profileData: any = null;
      
      if (responseData && responseData.success === true && responseData.data) {
        // Standard wrapped response: {success: true, data: {...}}
        profileData = {
          ...responseData.data,
          currentServices: responseData.data.currentServices || [],
          backupBids: responseData.data.backupBids || [],
          previousServiceRequests: responseData.data.previousServiceRequests || [],
          nextServiceRequests: responseData.data.nextServiceRequests || []
        };
      } else if (responseData && responseData.success === false) {
        // Error response: {success: false, error: "..."}
        setError(responseData.error || 'Failed to fetch customer profile');
        return;
      } else if (responseData && responseData.customer) {
        // Direct data response with customer property: {customer: {...}, backupBids: [...]}
        profileData = {
          ...responseData,
          currentServices: responseData.currentServices || [],
          backupBids: responseData.backupBids || [],
          previousServiceRequests: responseData.previousServiceRequests || [],
          nextServiceRequests: responseData.nextServiceRequests || []
        };
      } else if (responseData && (responseData.backupBids !== undefined || responseData.currentServices !== undefined)) {
        // Direct data response (already the data object)
        profileData = {
          ...responseData,
          currentServices: responseData.currentServices || [],
          backupBids: responseData.backupBids || [],
          previousServiceRequests: responseData.previousServiceRequests || [],
          nextServiceRequests: responseData.nextServiceRequests || []
        };
      } else {
        // Fallback - assume the response itself is the profile data
        profileData = {
          ...responseData,
          currentServices: responseData?.currentServices || [],
          backupBids: responseData?.backupBids || [],
          previousServiceRequests: responseData?.previousServiceRequests || [],
          nextServiceRequests: responseData?.nextServiceRequests || []
        };
      }
      
      if (profileData) {
        console.log('[CUSTOMER-PROFILE] ✅ Profile data loaded:', {
          hasBackupBids: !!profileData.backupBids,
          backupBidsCount: profileData.backupBids?.length || 0,
          backupBidsType: Array.isArray(profileData.backupBids) ? 'array' : typeof profileData.backupBids,
          hasCurrentServices: !!profileData.currentServices,
          currentServicesCount: profileData.currentServices?.length || 0,
          currentServicesType: Array.isArray(profileData.currentServices) ? 'array' : typeof profileData.currentServices,
          profileKeys: Object.keys(profileData),
          backupBidsSample: profileData.backupBids?.[0] || 'none',
          currentServicesSample: profileData.currentServices?.[0] || 'none',
          backupBidsRaw: profileData.backupBids
        });
        
        // Debug: Log current services
        if (!profileData.currentServices || !Array.isArray(profileData.currentServices) || profileData.currentServices.length === 0) {
          console.warn('[CUSTOMER-PROFILE] ⚠️ No current services found in profile data.');
          console.warn('[CUSTOMER-PROFILE] ⚠️ Current services value:', profileData.currentServices);
          console.warn('[CUSTOMER-PROFILE] ⚠️ Response data keys:', Object.keys(responseData || {}));
        } else {
          console.log('[CUSTOMER-PROFILE] ✅ Current services found:', profileData.currentServices.length);
          console.log('[CUSTOMER-PROFILE] ✅ Current services sample:', profileData.currentServices[0]);
        }
        
        // Debug: Log the full profile structure if no backup bids
        if (!profileData.backupBids || !Array.isArray(profileData.backupBids) || profileData.backupBids.length === 0) {
          console.warn('[CUSTOMER-PROFILE] ⚠️ No backup bids found in profile data.');
        }
        setProfile(profileData);
      }
    } catch (err: any) {
      console.error('Error fetching customer profile:', err);
      setError(err.response?.data?.error || 'Failed to fetch customer profile');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open && customerId) {
      console.log(`[CUSTOMER-PROFILE] 🚀 Dialog opened for customer: ${customerId}`);
      fetchCustomerProfile();
    } else {
      console.log(`[CUSTOMER-PROFILE] ⏸️ Dialog closed or no customerId. open: ${open}, customerId: ${customerId}`);
    }
  }, [open, customerId, fetchCustomerProfile]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatAddress = (address: any) => {
    if (!address) return 'N/A';
    return `${address.street}, ${address.city}, ${address.state} ${address.zipCode}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'scheduled': return 'info';
      case 'in-progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getServiceFrequency = (service: any) => {
    // First check the service request's own frequency field
    // For seasonal contracts, this should now be the actual serviceFrequency (e.g., 'weekly'), not 'seasonal-contract'
    if (service.frequency) {
      const frequency = service.frequency;
      if (frequency === 'weekly') {
        return 'Weekly';
      } else if (frequency === 'bi-weekly') {
        return 'Bi-weekly';
      } else if (frequency === 'monthly') {
        return 'Monthly';
      } else if (frequency === 'seasonal-contract') {
        // For seasonal contracts, check the accepted bid's serviceFrequency
        // Fall through to check bid data
      } else if (frequency === 'per-instance') {
        return 'One-time';
      }
    }
    
    // Helper function to extract actual frequency from service pricing
    const getActualFrequency = (sp: any): string | null => {
      // For seasonal contracts, use the actual serviceFrequency
      if ((sp.isSeasonalContract || sp.frequency === 'seasonal-contract') && sp.seasonalContractDetails?.serviceFrequency) {
        return sp.seasonalContractDetails.serviceFrequency;
      }
      return sp.frequency;
    };
    
    // Fallback: check the accepted bid's service pricing
    if (service.acceptedBidId?.servicePricing) {
      for (const sp of service.acceptedBidId.servicePricing) {
        const actualFreq = getActualFrequency(sp);
        if (actualFreq === 'weekly') return 'Weekly';
        if (actualFreq === 'bi-weekly') return 'Bi-weekly';
        if (actualFreq === 'monthly') return 'Monthly';
        if (actualFreq === 'per-instance') return 'One-time';
      }
    }
    
    // Additional fallback: check if service has acceptedBid data
    if (service.acceptedBid?.servicePricing) {
      for (const sp of service.acceptedBid.servicePricing) {
        const actualFreq = getActualFrequency(sp);
        if (actualFreq === 'weekly') return 'Weekly';
        if (actualFreq === 'bi-weekly') return 'Bi-weekly';
        if (actualFreq === 'monthly') return 'Monthly';
        if (actualFreq === 'per-instance') return 'One-time';
      }
    }
    
    return 'One-time';
  };

  const getServicePrice = (service: any) => {
    console.log('Service data for price calculation:', {
      serviceId: service._id,
      servicePrice: service.price,
      acceptedBidId: service.acceptedBidId,
      acceptedBid: service.acceptedBid,
      fullService: service
    });
    
    // First check the service request's own price field
    if (service.price) {
      return formatCurrency(service.price);
    }
    
    // Fallback: check the accepted bid's price
    if (service.acceptedBidId?.price) {
      return formatCurrency(service.acceptedBidId.price);
    }
    
    // Additional fallback: check if service has acceptedBid data
    if (service.acceptedBid?.price) {
      return formatCurrency(service.acceptedBid.price);
    }
    
    return 'N/A';
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleTransferClick = async () => {
    setTransferDialogOpen(true);
    setLoadingProviders(true);
    try {
      const response = await apiService.get('/admin/providers-summary');
      if (response.success && response.data) {
        setProviders((response.data as any).providers || []);
      }
    } catch (err: any) {
      setError('Failed to load providers');
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleTransferCancel = () => {
    setTransferDialogOpen(false);
    setTransferForm({
      newProviderId: '',
      reason: '',
      notes: '',
      customFeePercentage: '',
    });
  };

  const handleTransferConfirm = async () => {
    if (!transferForm.newProviderId) {
      setError('Please select a provider');
      return;
    }

    setTransferring(true);
    setError(null);
    try {
      const payload: any = {
        newProviderId: transferForm.newProviderId,
      };
      if (transferForm.reason) payload.reason = transferForm.reason;
      if (transferForm.notes) payload.notes = transferForm.notes;
      if (transferForm.customFeePercentage) {
        payload.customFeePercentage = parseFloat(transferForm.customFeePercentage);
      }

      const response = await apiService.post(`/admin/customers/${customerId}/transfer`, payload) as any;
      if (response.success) {
        setTransferDialogOpen(false);
        setTransferForm({
          newProviderId: '',
          reason: '',
          notes: '',
          customFeePercentage: '',
        });
        // Refresh customer profile
        fetchCustomerProfile();
        // Show success message
        const responseData = response.data as any;
        alert(`Customer successfully transferred to ${responseData?.newProviderName || 'new provider'}. ${response.message || ''}`);
      } else {
        setError(response.error || 'Failed to transfer customer');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to transfer customer');
    } finally {
      setTransferring(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const response = await apiService.delete(`/customers/${customerId}`);
      if (response.success) {
        setDeleteDialogOpen(false);
        if (onDelete) {
          onDelete();
        }
        onClose();
      } else {
        setError(response.error || 'Failed to delete customer');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Person />
            <Typography variant="h6">
              {profile ? `${profile.customer.firstName} ${profile.customer.lastName}` : 'Customer Profile'}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton
              onClick={handleTransferClick}
              color="primary"
              size="small"
              title="Transfer Customer to Another Provider"
            >
              <SwapHoriz />
            </IconButton>
            <IconButton
              onClick={handleDeleteClick}
              color="error"
              size="small"
              title="Delete Customer Account"
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {profile && (
          <Box>
            {/* Customer Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Person color="primary" />
                      <Typography variant="body2">
                        <strong>{profile.customer.firstName} {profile.customer.lastName}</strong>
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Email color="primary" />
                      <Typography variant="body2">{profile.customer.email}</Typography>
                    </Box>
                    {profile.customer.phone && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Phone color="primary" />
                        <Typography variant="body2">{profile.customer.phone}</Typography>
                      </Box>
                    )}
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationOn color="primary" />
                      <Typography variant="body2">
                        {formatAddress(profile.customer.address)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <AttachMoney color="success" />
                      <Typography variant="body2">
                        <strong>Total Spent:</strong> {formatCurrency(profile.totalSpent)}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Work color="info" />
                      <Typography variant="body2">
                        <strong>Total Jobs:</strong> {profile.totalJobs}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Schedule color="primary" />
                      <Typography variant="body2">
                        <strong>Member Since:</strong> {formatDate(profile.customer.createdAt.toString())}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Services - Grouped by Address */}
            {(() => {
              console.log('[CUSTOMER-PROFILE-UI] Rendering Current Services:', {
                hasProfile: !!profile,
                hasCurrentServices: !!profile?.currentServices,
                isArray: Array.isArray(profile?.currentServices),
                length: profile?.currentServices?.length || 0,
                currentServices: profile?.currentServices
              });
              
              if (!profile || !profile.currentServices || !Array.isArray(profile.currentServices) || profile.currentServices.length === 0) {
                return (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Services
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No current services
                      </Typography>
                    </CardContent>
                  </Card>
                );
              }
              
              // Group services by address
              const servicesByAddress: Record<string, any[]> = {};
              profile.currentServices.forEach((service: any) => {
                const addressKey = service.address 
                  ? `${service.address.street}, ${service.address.city}, ${service.address.state} ${service.address.zipCode}`
                  : 'Unknown Address';
                if (!servicesByAddress[addressKey]) {
                  servicesByAddress[addressKey] = [];
                }
                servicesByAddress[addressKey].push(service);
              });
              
              const addressKeys = Object.keys(servicesByAddress);
              
              return (
                <>
                  {addressKeys.map((addressKey, addressIndex) => {
                    const addressServices = servicesByAddress[addressKey];
                    const firstService = addressServices[0];
                    const address = firstService.address;
                    
                    return (
                      <Card key={addressIndex} sx={{ mb: 3 }}>
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <LocationOn color="primary" />
                            <Typography variant="h6">
                              {address ? `${address.street}, ${address.city}, ${address.state} ${address.zipCode}` : addressKey}
                            </Typography>
                          </Box>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Service</TableCell>
                                <TableCell>Provider</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Frequency</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Next Scheduled</TableCell>
                                <TableCell>Last Completed</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {addressServices.map((service: any, index: number) => {
                          console.log(`[CUSTOMER-PROFILE-UI] Rendering service ${index}:`, {
                            serviceId: service.id || service._id,
                            description: service.description,
                            status: service.status,
                            hasProviderId: !!service.providerId,
                            providerId: service.providerId
                          });
                          
                          const serviceId = service.id || service._id?.toString() || service._id || `service-${index}`;
                          const provider = service.providerId as any;
                          
                          // Handle both populated object and ID string
                          let providerName = 'N/A';
                          if (provider) {
                            if (typeof provider === 'string') {
                              providerName = provider; // If it's just an ID string
                            } else if (provider.businessName) {
                              providerName = provider.businessName;
                            } else if (provider.firstName && provider.lastName) {
                              providerName = `${provider.firstName} ${provider.lastName}`;
                            } else if (service.providerName) {
                              providerName = service.providerName; // Use providerName if already extracted
                            }
                          }
                          
                          // Determine next scheduled date (for active services) or last completed date
                          const nextScheduledDate = service.status !== 'completed' && service.scheduledDate 
                            ? service.scheduledDate 
                            : null;
                          const lastCompletedDate = service.completedDate || null;
                          
                          return (
                            <TableRow key={serviceId}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {service.serviceName || service.serviceType?.name || 'Service'}
                                </Typography>
                              </TableCell>
                          <TableCell>
                                {providerName}
                          </TableCell>
                          <TableCell>
                            <Chip 
                                  label={service.status === 'completed' ? 'Completed' : 
                                         service.status === 'accepted' ? 'Accepted' :
                                         service.status === 'scheduled' ? 'Scheduled' :
                                         service.status === 'in-progress' ? 'In Progress' : service.status} 
                              color={getStatusColor(service.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {getServiceFrequency(service)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {getServicePrice(service)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                                {nextScheduledDate 
                                  ? formatDate(nextScheduledDate.toString()) 
                                  : service.status === 'completed' 
                                    ? 'N/A' 
                                    : 'Not scheduled'}
                              </TableCell>
                              <TableCell>
                                {lastCompletedDate 
                                  ? formatDate(lastCompletedDate.toString()) 
                                  : 'N/A'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              );
            })()}

            {/* Service Request History for Violations */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Request History (for Violations)
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Previous and next service requests for tracking violations
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Previous Service Requests */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Previous Service Requests
                    </Typography>
                    {profile && profile.previousServiceRequests && Array.isArray(profile.previousServiceRequests) && profile.previousServiceRequests.length > 0 ? (
                      <Box>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Service Request ID</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Scheduled Date</TableCell>
                              <TableCell>Completed Date</TableCell>
                              <TableCell>Provider</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {profile.previousServiceRequests.map((sr: any) => (
                              <TableRow key={sr.id}>
                                <TableCell>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontFamily: 'monospace', 
                                      fontSize: '0.75rem', 
                                      wordBreak: 'break-all',
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(sr.id);
                                      // Optional: Show a toast notification
                                    }}
                                    title="Click to copy Service Request ID"
                                  >
                                    {sr.id || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={sr.status || 'N/A'} 
                                    size="small"
                                    color={
                                      sr.status === 'completed' ? 'success' :
                                      sr.status === 'cancelled' ? 'error' :
                                      sr.status === 'pending_cancellation' ? 'warning' :
                                      'default'
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {sr.scheduledDate ? formatDate(sr.scheduledDate.toString()) : 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {sr.completedDate ? formatDate(sr.completedDate.toString()) : 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    {sr.providerName || 'N/A'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No previous service requests
                      </Typography>
                    )}
                  </Grid>

                  {/* Next Service Requests */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Next/Upcoming Service Requests
                    </Typography>
                    {profile && profile.nextServiceRequests && Array.isArray(profile.nextServiceRequests) && profile.nextServiceRequests.length > 0 ? (
                      <Box>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Service Request ID</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Scheduled Date</TableCell>
                              <TableCell>Provider</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {profile.nextServiceRequests.map((sr: any) => (
                              <TableRow key={sr.id}>
                                <TableCell>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontFamily: 'monospace', 
                                      fontSize: '0.75rem', 
                                      wordBreak: 'break-all',
                                      cursor: 'pointer',
                                      '&:hover': { textDecoration: 'underline' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(sr.id);
                                      // Optional: Show a toast notification
                                    }}
                                    title="Click to copy Service Request ID"
                                  >
                                    {sr.id || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={sr.status || 'N/A'} 
                                    size="small"
                                    color={
                                      sr.status === 'scheduled' ? 'primary' :
                                      sr.status === 'in-progress' ? 'warning' :
                                      'default'
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {sr.scheduledDate ? formatDate(sr.scheduledDate.toString()) : 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    {sr.providerName || 'N/A'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                        No upcoming service requests
                  </Typography>
                )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Backup Bids */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Backup Bids ({profile.backupBids?.length || 0})
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  These are backup providers available if the primary provider doesn't complete the service
                </Typography>
                
                {profile && profile.backupBids && Array.isArray(profile.backupBids) && profile.backupBids.length > 0 ? (
                  <Box>
                    {profile.backupBids.map((backupBid, index) => (
                      <Accordion key={backupBid.id}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="subtitle1">
                              Service #{index + 1} - {formatCurrency(backupBid.acceptedBidPrice)}
                            </Typography>
                            <Chip 
                              label={`${backupBid.backupBids.length} backup options`}
                              color="info"
                              size="small"
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Accepted Bid Price:</strong> {formatCurrency(backupBid.acceptedBidPrice)}
                            </Typography>
                          </Box>
                          
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Provider</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Bid Date</TableCell>
                                <TableCell>Notes</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {backupBid.backupBids.map((backup, bidIndex) => (
                                <TableRow key={backup.bidId}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {backup.providerName}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {backup.providerPhone}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {backup.providerEmail}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {formatCurrency(backup.price)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {formatDate(backup.bidCreatedAt.toString())}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                      {backup.notes || 'No notes'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No backup bids available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Transfer Customer Dialog */}
      <Dialog
        open={transferDialogOpen}
        onClose={handleTransferCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Transfer Customer to Another Provider</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Transfer all active services for this customer to a new provider. This will:
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Transfer all active service requests</li>
              <li>Transfer onboarded customer records (if applicable)</li>
              <li>Reject the old provider's bids</li>
              <li>Create new accepted bids for the new provider</li>
              <li>Set platform fee to 1% (incentive rate) unless custom fee is specified</li>
            </ul>
          </DialogContentText>

          {profile?.currentServices && profile.currentServices.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This customer has no active services to transfer.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>New Provider *</InputLabel>
            <Select
              value={transferForm.newProviderId}
              onChange={(e) => setTransferForm({ ...transferForm, newProviderId: e.target.value })}
              disabled={loadingProviders || transferring}
              label="New Provider *"
            >
              {loadingProviders ? (
                <MenuItem disabled>Loading providers...</MenuItem>
              ) : providers.length === 0 ? (
                <MenuItem disabled>No providers available</MenuItem>
              ) : (
                providers.map((provider) => (
                  <MenuItem key={provider._id} value={provider._id}>
                    {provider.businessName || `${provider.firstName} ${provider.lastName}`}
                    {provider.email && ` (${provider.email})`}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Reason for Transfer"
            value={transferForm.reason}
            onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
            disabled={transferring}
            sx={{ mb: 2 }}
            placeholder="e.g., Provider no longer servicing customers, Poor customer service"
            helperText="Optional: Reason for transferring this customer"
          />

          <TextField
            fullWidth
            label="Additional Notes"
            value={transferForm.notes}
            onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
            disabled={transferring}
            multiline
            rows={3}
            sx={{ mb: 2 }}
            placeholder="Any additional information about this transfer"
            helperText="Optional: Additional notes for audit trail"
          />

          <TextField
            fullWidth
            label="Custom Platform Fee Percentage"
            value={transferForm.customFeePercentage}
            onChange={(e) => setTransferForm({ ...transferForm, customFeePercentage: e.target.value })}
            disabled={transferring}
            type="number"
            inputProps={{ min: 0, max: 50, step: 0.1 }}
            sx={{ mb: 2 }}
            placeholder="10"
            helperText="Optional: Default is 1% (incentive rate). Enter 0-50."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTransferCancel} disabled={transferring}>
            Cancel
          </Button>
          <Button
            onClick={handleTransferConfirm}
            color="primary"
            variant="contained"
            disabled={transferring || !transferForm.newProviderId || loadingProviders}
          >
            {transferring ? 'Transferring...' : 'Transfer Customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Customer Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this customer account? This action cannot be undone.
            {profile?.currentServices && profile.currentServices.length > 0 && (
              <Box mt={2}>
                <Alert severity="warning">
                  This customer has {profile.currentServices.length} active service(s). 
                  You must cancel or complete all active services before deleting this account.
                </Alert>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={deleting}
            variant="contained"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CustomerProfileDialog;
